import {
  ChevronLeft,
  ChevronRight,
  Crosshair,
  Minimize2,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/utils/cn'
import { DateTimePicker } from './DateTimePicker'
import type { PlaybackController } from './usePlaybackController'
import { PLAYBACK_SPEEDS } from './usePlaybackController'

export interface TimeRangeFilterProps {
  /** Total data extent — the absolute axis. Handles and viewWindow live inside this. */
  bounds: { start: number; end: number }
  /** Currently selected range; `null` means "no filter (full bounds)". */
  value: { start: number; end: number } | null
  onChange: (range: { start: number; end: number } | null) => void
  /** Optional pre-computed density bins (count is intensity 0..1). */
  density?: { count: number }[]
  /** Total entities considered. */
  total: number
  /** Entities currently within the selected range. */
  visibleCount: number
  /** Drives label and accent. */
  view: 'graph' | 'map' | 'timeline'
  /**
   * Visible slice of `bounds`. When provided the axis renders this slice
   * (not the full bounds) — used by playback to zoom into a fine-grained
   * window without losing the wider context. When omitted, the axis
   * spans the full bounds and the strip behaves as a pure filter.
   */
  viewWindow?: { start: number; end: number }
  /**
   * When set, wheel-scroll on the scrubber adjusts `viewWindow` (zoom).
   * Caller must own viewWindow state — typically a Zustand slice — and
   * apply the requested update from `onViewWindowChange`.
   */
  zoomable?: boolean
  onViewWindowChange?: (window: { start: number; end: number } | null) => void
  /** Reset to bounds when caller can recompute fresh from data. */
  onAutoFit?: () => void
  /** Override the "in window" wording (e.g. "events", "pins"). Defaults to "in window". */
  countSuffix?: string
  /**
   * Raw event timestamps used by the date-time picker to dot the days
   * that contain events. Optional — when omitted the picker shows no
   * dots but is otherwise identical.
   */
  timestamps?: number[]
  /**
   * Playback controller — typically `usePlaybackController()`. When
   * provided, the strip grows transport controls and renders a playhead
   * overlay; without it the strip is the simple filter.
   */
  playback?: PlaybackController
  /**
   * Optional content rendered in the meta row's left cell, right after
   * the view chip. Used by the map view to anchor a Filter/Playback mode
   * toggle there — the position stays put across mode flips so the
   * control never moves under the cursor.
   */
  leftSlot?: import('react').ReactNode
  className?: string
}

const VIEW_LABEL: Record<TimeRangeFilterProps['view'], string> = {
  graph: 'Graph',
  map: 'Map',
  timeline: 'Timeline',
}

const PRESETS = [
  { label: '24h', ms: 24 * 3_600_000 },
  { label: '7d', ms: 7 * 24 * 3_600_000 },
  { label: '30d', ms: 30 * 24 * 3_600_000 },
  { label: '90d', ms: 90 * 24 * 3_600_000 },
] as const

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MIN_RANGE_MS = 60_000
const MIN_VIEW_MS = 60_000 // 1-minute floor on wheel-zoom (also a UnifiedTimeline invariant)

function formatDuration(ms: number): string {
  if (ms <= 0) return '0s'
  const totalSec = Math.round(ms / 1000)
  if (totalSec < 60) return `${totalSec}s`
  const totalMin = Math.round(totalSec / 60)
  if (totalMin < 60) return `${totalMin}m`
  const totalHr = totalMin / 60
  if (totalHr < 24) return `${Math.round(totalHr)}h`
  const totalDays = totalHr / 24
  if (totalDays < 2) {
    const days = Math.floor(totalDays)
    return `${days}d ${Math.round((totalDays - days) * 24)}h`
  }
  if (totalDays < 60) return `${Math.round(totalDays)}d`
  const totalMo = totalDays / 30
  if (totalMo < 24) return `${Math.round(totalMo)}mo`
  return `${(totalDays / 365).toFixed(1)}y`
}

function formatHeadline(ms: number, scaleMs: number): string {
  const d = new Date(ms)
  const dd = String(d.getDate()).padStart(2, '0')
  const mo = MONTHS[d.getMonth()] ?? '—'
  const yr = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  if (scaleMs < 86_400_000) return `${mo} ${dd} · ${hh}:${mm}`
  return `${mo} ${dd}, ${yr} · ${hh}:${mm}`
}

function formatTick(ms: number, scaleMs: number): string {
  const d = new Date(ms)
  const dd = String(d.getDate()).padStart(2, '0')
  if (scaleMs < 86_400_000) {
    const hh = String(d.getHours()).padStart(2, '0')
    const mm = String(d.getMinutes()).padStart(2, '0')
    return `${hh}:${mm}`
  }
  if (scaleMs < 60 * 86_400_000) {
    return `${MONTHS[d.getMonth()]} ${dd}`
  }
  if (scaleMs < 2 * 365 * 86_400_000) {
    return `${MONTHS[d.getMonth()]} ${d.getFullYear() % 100}`
  }
  return `${d.getFullYear()}`
}

function formatPlayhead(ms: number, scaleMs: number): string {
  const d = new Date(ms)
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  if (scaleMs < 86_400_000) {
    const ss = String(d.getSeconds()).padStart(2, '0')
    return `${hh}:${mm}:${ss}`
  }
  return `${MONTHS[d.getMonth()]} ${dd} · ${hh}:${mm}`
}

function makeTicks(start: number, end: number, count = 6): { time: number; label: string }[] {
  const span = end - start
  if (span <= 0) return []
  const out: { time: number; label: string }[] = []
  for (let i = 0; i < count; i++) {
    const t = start + (span * i) / (count - 1)
    out.push({ time: t, label: formatTick(t, span) })
  }
  return out
}

export function TimeRangeFilter({
  bounds,
  value,
  onChange,
  density,
  total,
  visibleCount,
  view,
  viewWindow,
  zoomable = false,
  onViewWindowChange,
  onAutoFit,
  countSuffix = 'in window',
  timestamps,
  playback,
  leftSlot,
  className,
}: TimeRangeFilterProps) {
  const plotRef = useRef<HTMLDivElement | null>(null)
  const [startPickerOpen, setStartPickerOpen] = useState(false)
  const [endPickerOpen, setEndPickerOpen] = useState(false)
  const dragSideRef = useRef<'left' | 'right' | null>(null)

  // Visible axis = viewWindow when zoomed, otherwise full bounds. Handles
  // are positioned relative to whichever is currently the displayed axis;
  // the underlying `value` always stays in absolute ms.
  const axis = viewWindow ?? bounds
  const axisSpan = Math.max(0, axis.end - axis.start)
  const totalSpan = Math.max(0, bounds.end - bounds.start)
  const current = value ?? bounds
  const isFiltered = value !== null && (value.start !== bounds.start || value.end !== bounds.end)
  const isZoomed =
    viewWindow !== undefined && (viewWindow.start !== bounds.start || viewWindow.end !== bounds.end)

  // Percentage position relative to the visible axis.
  const pctOf = useCallback(
    (ms: number) => (axisSpan > 0 ? ((ms - axis.start) / axisSpan) * 100 : 0),
    [axis.start, axisSpan]
  )

  const leftPct = Math.max(-5, Math.min(105, pctOf(current.start)))
  const rightPct = Math.max(-5, Math.min(105, pctOf(current.end)))

  const playheadPct =
    playback?.playheadTime != null
      ? Math.max(-5, Math.min(105, pctOf(playback.playheadTime)))
      : null

  const ticks = useMemo(() => makeTicks(axis.start, axis.end), [axis.start, axis.end])

  // Refs read by the always-attached pointermove listener.
  const boundsRef = useRef(bounds)
  boundsRef.current = bounds
  const axisRef = useRef(axis)
  axisRef.current = axis
  const currentRef = useRef(current)
  currentRef.current = current
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // Single mount-time attach. Closure reads through refs so the listener
  // always sees fresh state — fixes the original drag-stops-after-one-move
  // bug from when listeners were re-attached per render.
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const side = dragSideRef.current
      const plot = plotRef.current
      if (!side || !plot) return
      const a = axisRef.current
      const s = a.end - a.start
      if (s <= 0) return
      const rect = plot.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      const newTime = a.start + ratio * s
      const cur = currentRef.current
      if (side === 'left') {
        onChangeRef.current({
          start: Math.min(newTime, cur.end - MIN_RANGE_MS),
          end: cur.end,
        })
      } else {
        onChangeRef.current({
          start: cur.start,
          end: Math.max(newTime, cur.start + MIN_RANGE_MS),
        })
      }
    }
    const onUp = () => {
      if (dragSideRef.current !== null) {
        dragSideRef.current = null
        document.body.style.cursor = ''
      }
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [])

  const startDrag = useCallback(
    (side: 'left' | 'right') => (e: ReactPointerEvent) => {
      e.preventDefault()
      e.stopPropagation()
      dragSideRef.current = side
      document.body.style.cursor = 'ew-resize'
    },
    []
  )

  // Wheel-zoom on the scrubber. Math mirrors the original UnifiedTimeline
  // implementation so the feel is identical for users coming from it.
  useEffect(() => {
    if (!zoomable || !onViewWindowChange) return
    const plot = plotRef.current
    if (!plot) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const b = boundsRef.current
      const v = axisRef.current
      const globalDuration = b.end - b.start
      const viewDuration = v.end - v.start
      if (globalDuration <= 0 || viewDuration <= 0) return
      const rect = plot.getBoundingClientRect()
      const cursorFraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      const zoomIn = e.deltaY < 0
      const factor = zoomIn ? 0.8 : 1.25
      let newDuration = viewDuration * factor
      newDuration = Math.max(MIN_VIEW_MS, Math.min(globalDuration, newDuration))
      // Snap to fully zoomed-out — anything within 1% of the total range
      // counts as "reset" so the user gets a crisp full view.
      if (newDuration >= globalDuration * 0.99) {
        onViewWindowChange(null)
        return
      }
      const anchor = v.start + cursorFraction * viewDuration
      let newStart = anchor - cursorFraction * newDuration
      let newEnd = anchor + (1 - cursorFraction) * newDuration
      if (newStart < b.start) {
        newStart = b.start
        newEnd = b.start + newDuration
      }
      if (newEnd > b.end) {
        newEnd = b.end
        newStart = b.end - newDuration
      }
      onViewWindowChange({ start: newStart, end: newEnd })
    }
    plot.addEventListener('wheel', onWheel, { passive: false })
    return () => plot.removeEventListener('wheel', onWheel)
  }, [zoomable, onViewWindowChange])

  // Click-to-seek on the scrubber (only when playback is wired up).
  const onScrubberClick = useCallback(
    (e: ReactPointerEvent) => {
      // Suppress clicks that landed on a handle (those start a drag instead).
      if (!playback || dragSideRef.current !== null) return
      const plot = plotRef.current
      if (!plot) return
      const rect = plot.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      const a = axisRef.current
      playback.onSeek(a.start + ratio * (a.end - a.start))
    },
    [playback]
  )

  const applyPreset = useCallback(
    (durationMs: number) => {
      const clamped = Math.min(durationMs, totalSpan)
      const mid = (current.start + current.end) / 2
      const half = clamped / 2
      let start = mid - half
      let end = mid + half
      if (start < bounds.start) {
        end += bounds.start - start
        start = bounds.start
      }
      if (end > bounds.end) {
        start -= end - bounds.end
        end = bounds.end
      }
      onChange({
        start: Math.max(bounds.start, start),
        end: Math.min(bounds.end, end),
      })
    },
    [bounds.start, bounds.end, current.start, current.end, totalSpan, onChange]
  )

  const startHeadline = formatHeadline(current.start, totalSpan)
  const endHeadline = formatHeadline(current.end, totalSpan)
  const playheadLabel =
    playback?.playheadTime != null ? formatPlayhead(playback.playheadTime, axisSpan) : null

  return (
    <div
      className={cn(
        'border-t border-border bg-card/95 backdrop-blur-md',
        'flex flex-col select-none',
        className
      )}
    >
      {/* META ROW. Fixed `min-h-7` (28px) keeps the row height identical
          across views and modes — without it, optional content like the
          ModeToggle (map), playback transport, or the "clear filter" X
          button each push the row a different amount of pixels, so the
          bottom edge of the strip would shift between Graph / Map.
          Below `lg` the row wraps — heading + count, then the date range on
          a line of its own, then the playback controls (`max-lg:order-*`):
          one line needs ~1100 px in playback mode, so tablets wrap too. */}
      <div className="flex min-h-7 flex-wrap items-center gap-x-3 gap-y-1.5 px-4 pt-2 pb-1.5 text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground lg:flex-nowrap lg:gap-3">
        <span className="inline-flex shrink-0 items-center gap-2 text-foreground">
          <span className="inline-block h-2 w-2 rounded-full bg-primary" aria-hidden />
          {/* When a `leftSlot` (typically a mode toggle) is provided, the
              slot itself becomes the heading — its active label tells the
              user which mode the strip is currently in, replacing the
              static "Time range" wording that would otherwise compete with
              it. Views without a toggle (graph, time-axis) still see the
              static label, which is honest for their single mode. */}
          {leftSlot ? (
            <span className="inline-flex items-center normal-case tracking-normal">{leftSlot}</span>
          ) : (
            <span className="font-medium tracking-[0.16em]">Time range</span>
          )}
          {/* Hidden on a phone: the page title already names the view, and
              the chip's width is what keeps the count on the heading line. */}
          <span className="ml-1 inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/15 px-1.5 py-0.5 text-[10px] tracking-[0.12em] text-primary max-md:hidden">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
            {VIEW_LABEL[view]}
          </span>
        </span>
        <span className="h-3 w-px shrink-0 bg-border max-lg:hidden" aria-hidden />
        <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-foreground normal-case tracking-normal max-lg:order-1 max-lg:basis-full max-md:text-[10px]">
          <Popover open={startPickerOpen} onOpenChange={setStartPickerOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                title="Click to set the start of the range"
                className={cn(
                  'rounded px-1 py-2 tabular-nums transition-colors md:py-0.5',
                  '-mx-1 hover:bg-accent hover:text-foreground',
                  'data-[state=open]:bg-primary/15 data-[state=open]:text-primary'
                )}
              >
                {startHeadline}
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" align="start" sideOffset={10} className="w-auto p-3">
              <DateTimePicker
                label="Set start"
                value={current.start}
                min={bounds.start}
                max={current.end - 60_000}
                timestamps={timestamps}
                onApply={next => {
                  onChange({ start: next, end: current.end })
                  setStartPickerOpen(false)
                }}
                onCancel={() => setStartPickerOpen(false)}
              />
            </PopoverContent>
          </Popover>
          <span className="text-muted-foreground/60">→</span>
          <Popover open={endPickerOpen} onOpenChange={setEndPickerOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                title="Click to set the end of the range"
                className={cn(
                  'rounded px-1 py-2 tabular-nums transition-colors md:py-0.5',
                  '-mx-1 hover:bg-accent hover:text-foreground',
                  'data-[state=open]:bg-primary/15 data-[state=open]:text-primary'
                )}
              >
                {endHeadline}
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" align="start" sideOffset={10} className="w-auto p-3">
              <DateTimePicker
                label="Set end"
                value={current.end}
                min={current.start + 60_000}
                max={bounds.end}
                timestamps={timestamps}
                onApply={next => {
                  onChange({ start: current.start, end: next })
                  setEndPickerOpen(false)
                }}
                onCancel={() => setEndPickerOpen(false)}
              />
            </PopoverContent>
          </Popover>
          <span className="text-[10px] italic text-muted-foreground md:text-[10.5px]">
            · span {formatDuration(current.end - current.start)}
          </span>
        </span>

        {/* Playback transport — only when caller provided a controller. */}
        {playback && (
          <span className="ml-auto inline-flex shrink-0 items-center gap-2 normal-case tracking-normal max-lg:contents">
            <PlaybackTransport playback={playback} />
            {zoomable && onViewWindowChange && (
              <ZoomControls
                isZoomed={isZoomed}
                onZoomIn={() => zoomAtCenter(0.8, bounds, axis, onViewWindowChange)}
                onZoomOut={() => zoomAtCenter(1.25, bounds, axis, onViewWindowChange)}
                onReset={() => onViewWindowChange(null)}
              />
            )}
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground max-lg:ml-auto">
              <span className="font-medium tabular-nums text-foreground">{visibleCount}</span>
              <span className="text-muted-foreground/70"> / </span>
              <span className="tabular-nums">{total}</span>
              <span className="ml-1.5">{countSuffix}</span>
            </span>
          </span>
        )}

        {/* No-playback meta tail: just the count + clear. */}
        {!playback && (
          <span className="ml-auto inline-flex shrink-0 items-center gap-2 normal-case tracking-normal">
            <span>
              <span className="font-medium tabular-nums text-foreground">{visibleCount}</span>
              <span className="text-muted-foreground/70"> / </span>
              <span className="tabular-nums">{total}</span>
              <span className="ml-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                {countSuffix}
              </span>
            </span>
            {isFiltered && (
              <button
                type="button"
                onClick={() => onChange(null)}
                title="Clear filter"
                className="inline-flex h-7 w-7 items-center justify-center rounded border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:h-5 md:w-5"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        )}
      </div>

      {/* SCRUBBER */}
      <div className="relative h-12 px-4">
        <div
          ref={plotRef}
          onPointerDown={onScrubberClick}
          className={cn(
            'absolute inset-x-4 top-0 bottom-3.5 overflow-hidden rounded-sm bg-muted/30',
            playback && 'cursor-pointer'
          )}
        >
          {density && density.length > 0 && (
            <svg
              viewBox={`0 0 ${density.length} 100`}
              preserveAspectRatio="none"
              className="absolute inset-0 h-full w-full"
            >
              {density.map((bin, i) => {
                const h = Math.max(2, bin.count * 100)
                return (
                  <rect
                    key={i}
                    x={i + 0.1}
                    y={100 - h}
                    width={0.8}
                    height={h}
                    className="fill-primary/40"
                  />
                )
              })}
            </svg>
          )}

          {/* veils outside the selected band */}
          <div
            className="pointer-events-none absolute inset-y-0 left-0 bg-background/65"
            style={{ width: `${Math.max(0, leftPct)}%` }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 bg-background/65"
            style={{ width: `${Math.max(0, 100 - rightPct)}%` }}
          />

          {/* selection band */}
          <div
            className="pointer-events-none absolute inset-y-0 border-y-2 border-primary/70 bg-primary/12"
            style={{ left: `${leftPct}%`, right: `${100 - rightPct}%` }}
          />

          {/* Playhead — diamond + stem. Rendered above veils so it's always visible. */}
          {playheadPct !== null && playheadPct >= -2 && playheadPct <= 102 && (
            <div
              className="pointer-events-none absolute inset-y-0 z-10"
              style={{ left: `${Math.max(0, Math.min(100, playheadPct))}%` }}
            >
              <span
                className="absolute inset-y-0 left-1/2 w-[1.5px] -translate-x-1/2 bg-primary"
                aria-hidden
              />
              <span
                className={cn(
                  'absolute -top-1.5 left-1/2 h-2.5 w-2.5 -translate-x-1/2 rotate-45 border border-card bg-primary',
                  playback?.isPlaying && 'animate-pulse'
                )}
                aria-hidden
              />
            </div>
          )}
        </div>

        {/* Bracket handles */}
        {(['left', 'right'] as const).map(side => {
          const pct = side === 'left' ? leftPct : rightPct
          const isLeft = side === 'left'
          return (
            <div
              key={side}
              role="slider"
              aria-label={isLeft ? 'Start of range' : 'End of range'}
              aria-valuemin={bounds.start}
              aria-valuemax={bounds.end}
              aria-valuenow={isLeft ? current.start : current.end}
              tabIndex={0}
              onPointerDown={startDrag(side)}
              className="absolute z-20 flex w-8 cursor-ew-resize touch-none items-center justify-center md:w-4"
              style={{
                left: `calc(1rem + ${pct}% * (100% - 2rem) / 100%)`,
                top: '0px',
                bottom: '14px',
                transform: 'translateX(-50%)',
              }}
            >
              <span
                className="pointer-events-none absolute inset-y-0 left-1/2 w-[3px] -translate-x-1/2 rounded-full bg-primary shadow-[0_0_0_1px_hsl(var(--background))]"
                aria-hidden
              />
              <span
                className={cn(
                  'pointer-events-none absolute top-0 h-[2px] w-2.5 rounded-full bg-primary',
                  isLeft ? 'left-1/2 -translate-x-[3px]' : 'left-1/2 -translate-x-[calc(100%-3px)]'
                )}
                aria-hidden
              />
              <span
                className={cn(
                  'pointer-events-none absolute bottom-0 h-[2px] w-2.5 rounded-full bg-primary',
                  isLeft ? 'left-1/2 -translate-x-[3px]' : 'left-1/2 -translate-x-[calc(100%-3px)]'
                )}
                aria-hidden
              />
              <span
                className="pointer-events-none absolute top-1/2 left-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-card bg-primary"
                aria-hidden
              />
            </div>
          )
        })}

        {/* Axis ticks */}
        <div className="pointer-events-none absolute inset-x-4 bottom-0 h-3">
          {ticks.map((t, i) => {
            const pct = pctOf(t.time)
            const inRange = t.time >= current.start && t.time <= current.end
            return (
              <span
                key={i}
                className={cn(
                  'absolute -translate-x-1/2 font-mono text-[9.5px] tabular-nums tracking-tight max-lg:whitespace-nowrap',
                  // Narrow screens: keep the edge labels inside the strip.
                  i === 0 && 'max-lg:translate-x-0',
                  i === ticks.length - 1 && 'max-lg:-translate-x-full',
                  inRange ? 'text-muted-foreground' : 'text-muted-foreground/45'
                )}
                style={{ left: `${pct}%` }}
              >
                {t.label}
              </span>
            )
          })}
        </div>
      </div>

      {/* PRESETS / ACTIONS — and live playhead readout when playing back. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 pt-1 pb-2 md:flex-nowrap md:gap-2">
        {onAutoFit && (
          <button
            type="button"
            onClick={onAutoFit}
            title="Fit to data cluster (skips outliers)"
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-2 md:py-1',
              'font-mono text-[10px] uppercase tracking-[0.14em] text-foreground',
              'hover:bg-accent transition-colors'
            )}
          >
            <Crosshair className="h-3 w-3" />
            Auto-fit
          </button>
        )}
        <span className="ml-1 font-mono text-[9.5px] uppercase tracking-[0.16em] text-muted-foreground max-md:hidden">
          Span
        </span>
        {PRESETS.map(p => (
          <button
            key={p.label}
            type="button"
            onClick={() => applyPreset(p.ms)}
            disabled={p.ms > totalSpan}
            className={cn(
              'rounded px-2 py-2 font-mono text-[10.5px] tracking-[0.06em] tabular-nums md:px-1.5 md:py-1',
              'text-muted-foreground hover:bg-accent hover:text-foreground',
              'disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground'
            )}
          >
            {p.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange(bounds)}
          className="rounded px-2 py-2 font-mono text-[10.5px] tracking-[0.06em] text-muted-foreground hover:bg-accent hover:text-foreground md:px-1.5 md:py-1"
        >
          All
        </button>
        {playheadLabel && (
          <span className="ml-auto inline-flex items-center gap-2 font-mono text-[10.5px] tabular-nums">
            <span className="text-[9.5px] uppercase tracking-[0.16em] text-muted-foreground">
              Playhead
            </span>
            <span className="rounded border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-primary">
              {playheadLabel}
            </span>
          </span>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Internal helpers — transport, speed pills, zoom buttons.
// ─────────────────────────────────────────────────────────────────────────

function PlaybackTransport({ playback }: { playback: PlaybackController }) {
  const { isPlaying, speed } = playback
  return (
    // Below `lg`, `contents` hands the two groups to the meta row, so the
    // buttons and the speed pills can wrap onto separate lines there.
    <span className="inline-flex items-center gap-1 max-lg:contents">
      <span className="inline-flex items-center gap-1 max-lg:order-2">
        <IconButton title="Skip to start (Home)" onClick={playback.onSkipToStart}>
          <SkipBack className="h-3.5 w-3.5" />
        </IconButton>
        <IconButton title="Step back (←)" onClick={playback.onStepBack}>
          <ChevronLeft className="h-3.5 w-3.5" />
        </IconButton>
        <button
          type="button"
          onClick={playback.onPlayPause}
          title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
          className={cn(
            'inline-flex h-8 items-center gap-1 rounded-md px-3 font-mono text-[10px] uppercase tracking-[0.14em] md:h-6 md:px-2',
            'transition-colors',
            isPlaying
              ? 'border border-border bg-muted text-foreground hover:bg-accent'
              : 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
          )}
        >
          {isPlaying ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
          {isPlaying ? 'Pause' : 'Play'}
        </button>
        <IconButton title="Step forward (→)" onClick={playback.onStepForward}>
          <ChevronRight className="h-3.5 w-3.5" />
        </IconButton>
        <IconButton title="Skip to end (End)" onClick={playback.onSkipToEnd}>
          <SkipForward className="h-3.5 w-3.5" />
        </IconButton>
      </span>
      <span className="ml-1 inline-flex items-center gap-0.5 rounded-md border border-border bg-card/70 p-0.5 max-lg:order-3 max-lg:ml-0">
        {PLAYBACK_SPEEDS.map(s => (
          <button
            key={s}
            type="button"
            onClick={() => playback.onSpeedChange(s)}
            title={`Playback ${s}×`}
            className={cn(
              'rounded-sm px-2.5 py-1.5 font-mono text-[10px] tabular-nums tracking-[0.04em] md:px-1.5 md:py-0.5',
              'transition-colors',
              speed === s
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
            )}
          >
            {s}×
          </button>
        ))}
      </span>
    </span>
  )
}

function ZoomControls({
  isZoomed,
  onZoomIn,
  onZoomOut,
  onReset,
}: {
  isZoomed: boolean
  onZoomIn: () => void
  onZoomOut: () => void
  onReset: () => void
}) {
  return (
    <span className="inline-flex items-center gap-0.5 max-lg:order-4 max-lg:ml-auto">
      <IconButton title="Zoom in" onClick={onZoomIn}>
        <ZoomIn className="h-3.5 w-3.5" />
      </IconButton>
      <IconButton title="Zoom out" onClick={onZoomOut}>
        <ZoomOut className="h-3.5 w-3.5" />
      </IconButton>
      {isZoomed && (
        <IconButton title="Reset zoom (0)" onClick={onReset}>
          <Minimize2 className="h-3.5 w-3.5" />
        </IconButton>
      )}
    </span>
  )
}

function IconButton({
  children,
  onClick,
  title,
}: {
  children: React.ReactNode
  onClick: () => void
  title: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="inline-flex h-8 w-8 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:h-6 md:w-6"
    >
      {children}
    </button>
  )
}

/** Centred wheel-zoom — used by the +/- buttons. Same math as the wheel handler. */
function zoomAtCenter(
  factor: number,
  bounds: { start: number; end: number },
  axis: { start: number; end: number },
  apply: (next: { start: number; end: number } | null) => void
): void {
  const globalDuration = bounds.end - bounds.start
  const viewDuration = axis.end - axis.start
  if (globalDuration <= 0 || viewDuration <= 0) return
  let newDuration = viewDuration * factor
  newDuration = Math.max(MIN_VIEW_MS, Math.min(globalDuration, newDuration))
  if (newDuration >= globalDuration * 0.99) {
    apply(null)
    return
  }
  const center = (axis.start + axis.end) / 2
  let newStart = center - newDuration / 2
  let newEnd = newStart + newDuration
  if (newStart < bounds.start) {
    newStart = bounds.start
    newEnd = bounds.start + newDuration
  }
  if (newEnd > bounds.end) {
    newEnd = bounds.end
    newStart = bounds.end - newDuration
  }
  apply({ start: newStart, end: newEnd })
}
